import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Cascader, Col, Form, Input, Modal, Radio, Row, Select, Space } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { DefaultOptionType } from 'rc-cascader/lib';
import { useNavigate } from 'react-router-dom';
import { Wrapper, useAsync } from '@maxtropy/components';

import { formatOptionData } from '@/shared/components/CascadingMultipleSelector/utils';
import { formatValueFn } from '@/shared/components/FieldItem/util';
import { getBatchDeviceResult } from '@/shared/api/edgeDevice';
import { getDeviceTypeData, AttributeTemplate } from '../../../api/template';
import { createBatchDeviceAttribute, updateBatchDeviceAttribute } from '../../../api/batch';
import { getPhysicalModelList, getRoot, PhysicalModelRes } from '../../../api/device';
import ErrorMsgModal, { ErrorMsg } from './ErrorMsgModal';
import SelectDevices from './SelectDevices';
import SelectTemplate from './SelectTemplate';
import SelectTemplateVersion from './SelectTemplateVersion';
import AttributeForm, { AttributeFormRef } from './AttributeForm';
import styles from './index.module.scss';

const { Option } = Select;

const routes = [{ name: 'Iot配置' }, { name: '设备管理' }, { name: '设备管理' }, { name: '批量配置固定属性' }];

const layout = {
  labelCol: { style: { width: 120, alignItems: 'center', display: 'flex', justifyContent: 'flex-end', height: 32 } },
  wrapperCol: { span: 9 },
};

interface FormValues {
  rootMcid?: string;
  deviceType?: number[];
  deviceIdList?: number[];
}

export enum ActionType {
  ADD = 0,
  EDIT = 1,
}

const BatchAttribute: React.FC = () => {
  const [form] = Form.useForm();
  const [invalidForm] = Form.useForm();
  const navigate = useNavigate();
  const attributeFormRef: MutableRefObject<AttributeFormRef | null> = useRef(null);

  const [rootMcid, setRootMcid] = useState<string>();
  const [deviceType, setDeviceType] = useState<Array<number> | undefined>();
  const [deviceTypeName, setDeviceTypeName] = useState<string>();
  const [actionType, setActionType] = useState<ActionType>(ActionType.ADD);
  const [template, setTemplate] = useState<AttributeTemplate>();
  const [templateVersion, setTemplateVersion] = useState<number>();
  const [errorMsg, setErrorMsg] = useState<ErrorMsg[]>();
  const [modalApi, modalContextHolder] = Modal.useModal();

  const makeLoadingModel = () =>
    modalApi.info({
      className: 'loading-modal',
      title: '正在执行',
      content: (
        <div className="loading-modal-content">
          <div className="loading-modal-icon">
            <LoadingOutlined />
          </div>
          <div className="loading-modal-txt">正在执行中</div>
        </div>
      ),
    });

  const customerData = useAsync(getRoot);
  const deviceTypeData = useAsync(getDeviceTypeData);
  const physicalModelId = Form.useWatch('physicalModelId', form);
  useEffect(() => {
    form.setFieldsValue({
      template: undefined,
      version: undefined,
      deviceIdList: undefined,
    });
    attributeFormRef.current?.form?.resetFields();
    setTemplate(undefined);
  }, [rootMcid, deviceType, actionType, form]);

  useEffect(() => {
    form.setFieldsValue({
      version: template,
      deviceIdList: undefined,
    });
    attributeFormRef.current?.form?.resetFields();
    setTemplateVersion(template?.version);
  }, [template, form]);

  useEffect(() => {
    form.setFieldsValue({
      deviceIdList: undefined,
    });
    attributeFormRef.current?.form?.resetFields();
  }, [templateVersion, form]);

  const onDeviceTypeSelect = (val: (string | number | null)[], selectedOptions: DefaultOptionType[]) => {
    setDeviceType(val as number[]);
    const _deviceTypeName = selectedOptions?.map(item => item.label).join('/');
    setDeviceTypeName(_deviceTypeName);
    form.setFieldsValue({
      physicalModelId: undefined,
    });
  };

  const onTemplateSelect = (val?: AttributeTemplate) => {
    setTemplate(val);
  };

  const confirmSubmit = () => {
    Promise.all([form.validateFields(), attributeFormRef.current?.form?.validateFields()]).then(() => {
      modalApi.confirm({
        icon: null,
        title: null,
        content: (
          <>
            <Form form={invalidForm} layout="vertical">
              <Form.Item label="请输入操作密码" name="password" rules={[{ required: true, message: '请输入操作密码' }]}>
                <Input.Password autoComplete="new-password" placeholder="请输入操作密码" />
              </Form.Item>
            </Form>
          </>
        ),
        onOk: () => {
          return invalidForm.validateFields().then(value => onSubmit(value.password));
        },
        afterClose: () => {
          invalidForm.resetFields();
        },
      });
    });
  };

  const handleError = (error: any) => {
    const { errorMessage, status } = error?.cause;
    if (status === 410) {
      const msg = JSON.parse(errorMessage);
      setErrorMsg(msg);
    } else {
      modalApi.error({
        title: errorMessage,
        okText: '确定',
      });
    }
  };

  const getFormValues = (password: number) => {
    const { deviceIdList } = form.getFieldsValue();
    const submitValues = {
      password,
      deviceIds: deviceIdList,
      templateId: template?.id,
      templateVersion,
    };
    const values = attributeFormRef.current?.form?.getFieldsValue();
    const templateFlatten = attributeFormRef.current?.templateFlatten;
    const checkedIds = attributeFormRef.current?.checkedIds;
    if (values && templateFlatten) {
      const list = Object.entries(values).map(([fieldId, value]) => {
        const findTemplate = templateFlatten.find(i => i.id === Number(fieldId));
        const formatValue = formatValueFn(value.value, findTemplate);
        return {
          fieldId: Number(fieldId),
          unitId: value.unitId,
          value: formatValue,
        };
      });
      return {
        ...submitValues,
        list: actionType === ActionType.ADD ? list : list.filter(item => checkedIds?.includes(item.fieldId)),
      };
    }
  };

  const fetchBatchDeviceResult = (res: { key: string }) =>
    new Promise((resolve, reject) => {
      const timerHandler = window.setInterval(async () => {
        try {
          const response = await getBatchDeviceResult(res.key);
          if (response.hasResult) {
            clearInterval(timerHandler);
          }
          resolve(response.hasResult);
        } catch (e) {
          clearInterval(timerHandler);
          reject(e);
        }
      }, 1000);
    });

  const onSubmit = async (password: number) => {
    const values = getFormValues(password);
    const modal = makeLoadingModel();
    console.log('values', values);
    if (values) {
      let parmas = {
        ...values,
        mcid: rootMcid,
      };
      const promise =
        actionType === ActionType.ADD ? createBatchDeviceAttribute(parmas) : updateBatchDeviceAttribute(parmas);
      promise
        .onError(error => {
          modal.destroy();
          handleError(error);
          throw error;
        })
        .then(res => {
          fetchBatchDeviceResult(res).then(() => {
            modal.destroy();
            modalApi.success({
              title: '批操作成功',
              okText: '确定',
              onOk: () => navigate(-1),
            });
          });
        });
    }
  };
  const [physicalModelList, setPhysicalModelList] = useState<PhysicalModelRes[]>([]); // all 物模型型号列表
  useEffect(() => {
    if (deviceType && deviceType[2]) {
      // 物模型型号
      getPhysicalModelList({ deviceTypeIds: [deviceType[2]] }).then(setPhysicalModelList);
    }
  }, [deviceType]);
  const objectModalTypeOptions = useMemo(() => {
    if (physicalModelList && physicalModelList.length > 0) {
      return physicalModelList.map(i => ({ label: `${i.modelNo}-${i.manufacturerName}`, value: i.id }));
    }
  }, [physicalModelList]);

  return (
    <Wrapper routes={routes}>
      <div className={styles.details}>
        <Form<FormValues> form={form} onFinish={confirmSubmit}>
          <Row gutter={10}>
            <Col span={8}>
              <Form.Item name="rootMcid" rules={[{ required: true, message: '请选择所属租户' }]} label="所属租户">
                <Select placeholder={'请选择租户'} onChange={(v: string) => setRootMcid(v)}>
                  {customerData?.map(item => (
                    <Option key={item.id} value={item.mcid}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={10}>
            <Col span={8}>
              <Form.Item name="deviceType" rules={[{ required: true, message: '请选择所属类目' }]} label="所属类目">
                <Cascader
                  options={formatOptionData(deviceTypeData)}
                  allowClear={false}
                  fieldNames={{ children: 'child' }}
                  onChange={onDeviceTypeSelect}
                  placeholder={'请选择所属类目'}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="physicalModelId" label="物模型型号">
                <Select
                  allowClear
                  optionFilterProp="label"
                  showSearch
                  options={objectModalTypeOptions}
                  disabled={!deviceType}
                  onChange={() => {
                    setTemplate(undefined);
                    form.setFieldsValue({
                      template: undefined,
                    });
                  }}
                  placeholder={deviceType ? '请选择' : '请先选择所属类目'}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="type"
            rules={[{ required: true, message: '请选择批操作类型' }]}
            label="批操作类型"
            initialValue={ActionType.ADD}
          >
            <Radio.Group onChange={e => setActionType(e.target.value)}>
              <Radio value={ActionType.ADD}>新增固定属性模板</Radio>
              <Radio value={ActionType.EDIT}>修改模板中属性值</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="template" rules={[{ required: true, message: '请选择固定属性模板' }]} label="固定属性模板">
            <SelectTemplate
              rootMcid={rootMcid}
              deviceType={deviceType}
              actionType={actionType}
              physicalModelId={physicalModelId}
              onChange={onTemplateSelect}
            />
          </Form.Item>
          {template && (
            <Form.Item name="version" rules={[{ required: true, message: '请选择固定属性模板' }]} label="模板版本号">
              <SelectTemplateVersion
                rootMcid={rootMcid}
                deviceType={deviceType}
                templateId={template?.id}
                actionType={actionType}
                onChange={value => setTemplateVersion(value?.version)}
              />
            </Form.Item>
          )}
          <Form.Item name="deviceIdList" rules={[{ required: true, message: '请选择设备' }]} label="设备选择">
            <SelectDevices
              rootMcid={rootMcid}
              deviceType={deviceType}
              templateId={template?.id}
              templateVersion={templateVersion}
              actionType={actionType}
              physicalModelId={physicalModelId}
              deviceTypeName={deviceTypeName}
            />
          </Form.Item>
          {template && (
            <>
              <AttributeForm
                ref={attributeFormRef}
                templateId={template?.id}
                templateVersion={templateVersion}
                actionType={actionType}
              />
            </>
          )}
          <Space className={styles.footer}>
            <Button htmlType="submit" type={'primary'}>
              完成
            </Button>
            <Button onClick={() => navigate(-1)}>取消</Button>
          </Space>
        </Form>
      </div>
      <ErrorMsgModal visible={errorMsg !== undefined} onCancel={() => setErrorMsg(undefined)} errorMsg={errorMsg} />
      {modalContextHolder}
    </Wrapper>
  );
};

export default BatchAttribute;
